import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { ApideckVault } from "@apideck/vault-js";
import {
  GET_SUPPORTED_INTEGRATIONS,
  GET_INTEGRATION_TOKEN,
  CONNECT_INTEGRATION,
  DISCONNECT_INTEGRATION,
} from "../../../graphql/integration";
import { Typography } from "@mui/material";
import { ConfirmDialog, toast } from "@outpave/ds";
import { Center, Columns, Stack } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import LoadingWithoutModal from "../../common/LoadingWithoutModal";
import { IntegrationService } from "../../../enums/IntegrationService";
import LinkModal from "../common/LinkModal";
import { useLocation, useNavigate } from "react-router-dom";
import { IntegrationAction } from "../../../enums/IntegrationAction";
import IntegrationSettingsDrawer from "./IntegrationSettings";
import useGetAccounts from "hooks/useGetAccounts";
import { useAllocationTypes } from "hooks/useAllocationTypes";
import IntegrationCard from "./IntegrationCard";
import {
  byTitle,
  comingSoonLast,
  entityMappingCount,
  ConnectionStatus,
  Integration,
  IntegrationConnection,
  isBrowsable,
  isConnected,
  matchesQuery,
  mergeConnections,
} from "utils/integrations/integrationConnections";

type SelectedIntegration = {
  title: string;
  integrationType: string;
  integrationService: string;
  sourceSystem: string;
  referenceId: string;
  linkToken: string;
  linkUrl: string;
  existingAccountId?: string;
};

/** How many times a settling mutation asks the accounts query to catch up. */
const REFETCH_ATTEMPTS = 2;

interface IntegrationsConnectorProps {
  /** Free text from the page header's search field. Filters both sections. */
  query?: string;
}

interface IntegrationSectionProps {
  title: string;
  emptyMessage: string;
  connections: IntegrationConnection[];
  columns: Record<string, number>;
  renderCard: (connection: IntegrationConnection) => React.ReactNode;
}

const IntegrationSection: React.FC<IntegrationSectionProps> = ({
  title,
  emptyMessage,
  connections,
  columns,
  renderCard,
}) => (
  <Stack gap="var(--space-5)">
    <Typography variant="h5" component="h2">
      {title}
    </Typography>
    {connections.length > 0 ? (
      <Columns count={columns}>{connections.map(renderCard)}</Columns>
    ) : (
      <Center sx={{ paddingBlock: "var(--space-9)" }}>
        <Typography
          variant="body1"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {emptyMessage}
        </Typography>
      </Center>
    )}
  </Stack>
);

function IntegrationsConnector({ query = "" }: IntegrationsConnectorProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const useParams = useRef(false);
  const autoReconnectTriggered = useRef(false);

  const [loading, setLoading] = useState(false);
  const [accountTypes, setAccountTypes] = useState<string[]>([]);
  const [integrationType, setIntegrationType] = useState("");
  const [drawerIntegrationType, setDrawerIntegrationType] = useState("");
  const [integration, setIntegration] = useState<SelectedIntegration | null>(
    null,
  );
  const [openConductor, setOpenConductor] = useState(false);
  const [isLinkOpening, setIsLinkOpening] = useState(false);
  // Optimistic status per integration type, cleared when a mutation settles.
  // Kept beside the derived list rather than inside it, because the list comes
  // from two queries that refetch and would overwrite it.
  const [pending, setPending] = useState<Record<string, ConnectionStatus>>({});
  const [openIntegrationsDrawer, setOpenIntegrationDrawer] = useState(false);
  const [disconnectModalState, setDisconnectModalState] = useState({
    isOpen: false,
    accountId: "",
    type: "",
  });

  const { data: integrations } = useQuery(GET_SUPPORTED_INTEGRATIONS, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setAccountTypes(
        data.integrations.map(
          (connection: Integration) => connection.integrationType,
        ),
      );
    },
  });

  const {
    externalAccounts: integrationData,
    loading: loadingIntegrationStatus,
    refetch: refetchIntegrationStatus,
  } = useGetAccounts({
    accountTypes,
    skip: !accountTypes?.length,
  });

  // One company-wide query for the whole grid, not one per card: an allocation
  // type carries the integration it is mapped to.
  const { allocationTypes } = useAllocationTypes({ showAll: true });

  const connections: IntegrationConnection[] = useMemo(
    () =>
      mergeConnections(
        integrations?.integrations ?? [],
        integrationData ?? [],
        pending,
      ),
    [integrations, integrationData, pending],
  );

  /**
   * Runs a mutation that carries an optimistic status, and retires that status
   * only once the accounts query has caught up.
   *
   * Two things go wrong if the entry is dropped the moment the mutation
   * returns: the card falls back to the status the cache still holds, which is
   * the one the mutation just changed, so it visibly rolls back until the
   * refetch lands; and clearing the whole map takes the optimistic status of
   * any other in-flight integration with it.
   */
  const settle = async (type: string, mutation: Promise<unknown>) => {
    let mutated = true;
    try {
      await mutation;
    } catch {
      // The mutation reports its own failure. This only stops the rejection
      // escaping as an unhandled one, since nothing awaits settle.
      mutated = false;
    }

    // A mutation that failed changed nothing, so the cache is still right and
    // the optimistic status goes at once. One that succeeded leaves the cache
    // holding the status it just changed, so the overlay has to outlive a
    // refetch that does not land, and is worth one retry. It is still retired
    // after that: holding it indefinitely is the stuck card this replaced.
    if (mutated) {
      for (let attempt = 0; attempt < REFETCH_ATTEMPTS; attempt++) {
        try {
          await refetchIntegrationStatus();
          break;
        } catch {
          // Next attempt, then the clear below either way.
        }
      }
    }

    setPending((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
  };

  useEffect(() => {
    if (useParams.current) return; // Prevent duplicate execution
    useParams.current = true;
    const params = new URLSearchParams(location.search);
    const integrationTypeParam = params.get("integrationType");
    const action = params.get("action");
    const publicToken = params.get("publicToken");

    if (
      action === IntegrationAction.CONNECT &&
      publicToken &&
      integrationTypeParam
    ) {
      setIntegrationType(integrationTypeParam);
      setPending({ [integrationTypeParam]: ConnectionStatus.CONNECTING });
      settle(
        integrationTypeParam,
        connectIntegration({
          variables: {
            type: integrationTypeParam.toUpperCase(),
            key: publicToken,
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [connectIntegration, { loading: connecting }] = useMutation(
    CONNECT_INTEGRATION,
    {
      onCompleted: () => {
        setIntegration(null);
        setOpenConductor(false);
      },
      onError: () => {
        toast.error(t("SomethingWentWrongDescriptionWithSupport"));
        setIntegration(null);
        setOpenConductor(false);
      },
    },
  );

  const [disconnectIntegration, { loading: disconnecting }] = useMutation(
    DISCONNECT_INTEGRATION,
    {
      onCompleted: () => {
        setIntegration(null);
      },
      onError: () => {
        toast.error(t("SomethingWentWrongDescriptionWithSupport"));
        setIntegration(null);
      },
    },
  );

  const [getIntegrationToken, { loading: gettingToken }] = useMutation(
    GET_INTEGRATION_TOKEN,
  );

  const handleConnect = async (
    title: string,
    integrationType: string,
    integrationService: string,
    sourceSystem: string,
    accountId?: string,
  ) => {
    setIsLinkOpening(false);
    // Before the await, not after: the busy overlay reads this, and the token
    // request is the slow half of the flow.
    setIntegrationType(integrationType);
    await getIntegrationToken({
      // On reconnect, pass the existing accountId so the server reuses that account's Vault consumer;
      // a fresh connect omits it and the server derives a stable one per company and integration type.
      variables: { type: integrationType, ...(accountId && { accountId }) },
    })
      .then((response) => {
        const integration = {
          title,
          integrationType,
          sourceSystem,
          integrationService: integrationService,
          referenceId: response.data.integrationToken.referenceId,
          linkToken: response.data.integrationToken.linkToken,
          linkUrl: response.data.integrationToken.linkUrl,
          ...(accountId && { existingAccountId: accountId }),
        };
        setIntegration(integration);
      })
      .catch((error: any) => {
        toast.error(`Failed to connect integration: ${error.message}`);
      });
  };

  // Deep-link auto-reconnect: when arriving from the disconnect popup with
  // ?integrationType=X&action=reconnect (only sent when a single integration is disconnected),
  // auto-open that integration's reconnect flow once the connection list has loaded.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") !== IntegrationAction.RECONNECT) {
      // No reconnect intent — reset the guard so a later deep-link (e.g. from the disconnect popup
      // while already on this page) can trigger again.
      autoReconnectTriggered.current = false;
      return;
    }
    if (autoReconnectTriggered.current) return;
    const typeParam = params.get("integrationType");
    const match = connections.find(
      (candidate) =>
        candidate.integrationType === typeParam &&
        candidate.connectionStatus === ConnectionStatus.DISCONNECTED &&
        candidate.accountId,
    );
    if (!match) return; // not loaded yet, or already reconnected / stale param → no-op

    autoReconnectTriggered.current = true;
    handleConnect(
      match.title,
      match.integrationType,
      match.integrationService,
      match.sourceSystem,
      match.accountId,
    );
    navigate("/admin/settings/integrations", { replace: true }); // strip params so refresh won't re-open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, location.search]);

  const handleOpenIntegrationDrawer = (
    integrationType: string,
    open: boolean,
  ) => {
    setOpenIntegrationDrawer(open);
    setDrawerIntegrationType(integrationType);
  };

  const handleClose = () => {
    setIntegration(null);
    setIsLinkOpening(false);
    setOpenConductor(false);
    refetchIntegrationStatus();
  };

  useEffect(() => {
    setLoading(connecting || disconnecting || gettingToken || isLinkOpening);
  }, [connecting, disconnecting, gettingToken, isLinkOpening]);

  useEffect(() => {
    if (integration) {
      if (integration.integrationService === IntegrationService.APIDECK) {
        openApideckVault(integration);
      } else if (
        integration.integrationService === IntegrationService.CONDUCTOR
      ) {
        if (integration?.linkUrl) {
          setIsLinkOpening(true);
          setOpenConductor(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration]);

  const openApideckVault = (integration: SelectedIntegration) => {
    if (!integration.linkToken) {
      setIntegration(null);
      return;
    }

    setIsLinkOpening(true);
    const { integrationType, referenceId, sourceSystem } = integration;

    // Trigger connect once, when the connection becomes "callable" (and close the Vault).
    let hasTriggeredConnect = false;
    let hasAuthorized = false;

    const triggerConnect = () => {
      if (hasTriggeredConnect) return;
      hasTriggeredConnect = true;
      setIsLinkOpening(false);
      // Close the Vault ourselves so the popup auto-closes on success (per Apideck's recommendation).
      ApideckVault.close();
      setPending((prev) => ({
        ...prev,
        [integrationType]: ConnectionStatus.CONNECTING,
      }));
      settle(
        integrationType,
        connectIntegration({
          variables: {
            type: integrationType.toUpperCase(),
            key: referenceId,
          },
        }),
      );
    };

    ApideckVault.open({
      token: integration.linkToken,
      unifiedApi: "accounting",
      serviceId: sourceSystem,
      autoStartAuthorization: true,
      onConnectionChange: (connection: any) => {
        if (
          connection?.state === "authorized" ||
          connection?.state === "callable"
        ) {
          hasAuthorized = true;
        }
        if (connection?.state === "callable") {
          triggerConnect();
        }
      },
      onClose: () => {
        if (hasTriggeredConnect) return;
        // Missed "callable" but the user finished authorizing — trigger connect anyway.
        if (hasAuthorized) {
          triggerConnect();
          return;
        }
        // User closed the Vault without authorizing — reset silently.
        setIsLinkOpening(false);
        setIntegration(null);
      },
    });
  };

  const openDisconnectModal = (
    isOpen: boolean,
    accountId: string,
    type: string,
  ) => {
    setDisconnectModalState({ isOpen, accountId, type });
  };
  const closeDisconnectModal = () => {
    setDisconnectModalState({ isOpen: false, accountId: "", type: "" });
  };

  const handleDisconnect = () => {
    // Read before closing: the close resets the state these come from.
    const { type, accountId } = disconnectModalState;
    setIntegrationType(type);
    setPending((prev) => ({ ...prev, [type]: ConnectionStatus.DISCONNECTING }));

    settle(type, disconnectIntegration({ variables: { accountId } }));

    closeDisconnectModal();
  };

  const visible = connections.filter((connection) =>
    matchesQuery(connection, query),
  );
  const connected = visible.filter(isConnected).sort(byTitle);
  const browsable = visible.filter(isBrowsable).sort(comingSoonLast);

  const renderCard = (connection: IntegrationConnection) => (
    <IntegrationCard
      key={connection.integrationType}
      connection={connection}
      busy={loading && connection.integrationType === integrationType}
      mapping={entityMappingCount(connection, allocationTypes)}
      onConnect={(target) =>
        handleConnect(
          target.title,
          target.integrationType,
          target.integrationService,
          target.sourceSystem,
        )
      }
      onReconnect={(target) =>
        handleConnect(
          target.title,
          target.integrationType,
          target.integrationService,
          target.sourceSystem,
          target.accountId,
        )
      }
      onManage={(target) =>
        handleOpenIntegrationDrawer(target.integrationType, true)
      }
      onDisconnect={(target) =>
        openDisconnectModal(
          true,
          target.accountId ?? "",
          target.integrationType,
        )
      }
    />
  );

  return (
    <Stack
      gap="var(--space-9)"
      sx={{
        // Scopes the first-load overlay, which is otherwise viewport-wide.
        position: "relative",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        // The shell's header does not pad the scroll region, so without this
        // the first heading sits against the search field and clips under it.
        paddingBlock: "var(--space-7) var(--space-9)",
      }}
    >
      {/* Three: a connected card carries an account name and a mapping count
          beside its action, so it needs the width. */}
      <IntegrationSection
        title={t("Connected")}
        emptyMessage={t("No integrations connected")}
        connections={connected}
        columns={{ xs: 1, sm: 2, lg: 3 }}
        renderCard={renderCard}
      />

      {/* Four: a browse card is a logo, a title, a category and one button,
          so it fits a narrower tile than a connected one. */}
      <IntegrationSection
        title={t("Browse platforms")}
        emptyMessage={t("No platforms match this search")}
        connections={browsable}
        columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
        renderCard={renderCard}
      />

      <LoadingWithoutModal
        open={loadingIntegrationStatus && connections.length === 0}
        contained
      />

      <LinkModal
        title={t("Connect Integration")}
        description={t("conductorLinkPrompt", {
          name: integration?.title ?? "",
          // Integration titles can carry "&" or "<". i18next escapes
          // interpolated values by default, and React escapes the text node
          // when it renders, so the escape here would show up as entities.
          interpolation: { escapeValue: false },
        })}
        link={integration?.linkUrl || ""}
        open={openConductor}
        handleCloseModal={handleClose}
      />

      <IntegrationSettingsDrawer
        integrationType={drawerIntegrationType}
        open={openIntegrationsDrawer}
        handleCloseDrawer={() => setOpenIntegrationDrawer(false)}
      />

      {/* Disconnecting drops the linked expense categories and allocations,
          and reconnecting does not bring them back, which is what makes this
          the danger default rather than primary. */}
      <ConfirmDialog
        open={disconnectModalState.isOpen}
        title={t("Disconnection Warning")}
        message={
          <Stack gap="var(--space-3)">
            <Typography variant="body2">
              {t("WarningDisconnectIntegration")}
            </Typography>
            {/* A list, not the checkboxes this used to draw: they were checked
                with no onChange, so they were focusable and unchangeable. */}
            <Stack
              component="ul"
              gap="var(--space-2)"
              // The baseline reset strips list markers, and without them these
              // three read as loose lines rather than as the set they are.
              sx={{
                margin: 0,
                paddingInlineStart: "var(--space-6)",
                "& > li": { listStyleType: "disc" },
              }}
            >
              <Typography component="li" variant="body2">
                {t("Cards (required)")}
              </Typography>
              <Typography component="li" variant="body2">
                {t("Card Requests (required)")}
              </Typography>
              <Typography component="li" variant="body2">
                {t("Card Presets (required)")}
              </Typography>
            </Stack>
            <Typography variant="body2">
              {t("impactedCardsCategoryNotice")}
            </Typography>
            <Typography variant="body2">
              {t("Do you wish to continue?")}
            </Typography>
          </Stack>
        }
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={handleDisconnect}
        onCancel={closeDisconnectModal}
      />
    </Stack>
  );
}

export default IntegrationsConnector;
